import {useState, type FormEvent} from 'react'
import {useTranslation} from 'react-i18next'
import {AlcanceRequisitos} from '@/components/alcance-requisitos'
import {DateField} from '@/components/date-field'
import {FieldError} from '@/components/field-error'
import {Button} from '@/components/ui/button'
import {Label} from '@/components/ui/label'
import {Textarea} from '@/components/ui/textarea'
import {Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle} from '@/components/ui/dialog'
import {validateRequired} from '@/lib/validation'
import type {PerfilAlcance} from '@/hooks/empresa/perfil_cargo/use-perfiles-cargo'
import {tope} from '@/lib/campos-texto'

/** Igual a `NOMBRES_DEL_RESUMEN` de `routers/perfiles_cargo.py`; el `slice` es cinturón por si
 * el sobre trae más. */
const NOMBRES_A_LA_VISTA = 3

export function PerfilAlcanceDialog({
  alcance,
  exigible,
  motivo,
  errorFirma,
  pendiente,
  onExigible,
  onMotivo,
  onCerrar,
  onFirmar,
}: {
  alcance: PerfilAlcance | null
  exigible: string
  motivo: string
  errorFirma: string | null
  pendiente: boolean
  onExigible: (valor: string) => void
  onMotivo: (valor: string) => void
  onCerrar: () => void
  onFirmar: (evento: FormEvent) => void
}) {
  const {t} = useTranslation()
  // Vacía, el backend responde un 422 que en el diálogo se leería como «no pasó nada».
  const [errorFecha, setErrorFecha] = useState<string | null>(null)
  function enviar(evento: FormEvent) {
    const falta = validateRequired(exigible)
    setErrorFecha(falta)
    if (falta) {
      evento.preventDefault()
      return
    }
    onFirmar(evento)
  }
  return (
    <Dialog open={alcance !== null} onOpenChange={(abierto) => !abierto && onCerrar()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t('perfilCargo.alcance.titulo')}</DialogTitle>
          <DialogDescription>
            {alcance && alcance.total_personas > 0
              ? t('perfilCargo.alcance.personas', {
                  count: alcance.total_personas,
                  nombres: alcance.personas
                    .slice(0, NOMBRES_A_LA_VISTA)
                    .map((persona) => persona.nombre)
                    .join(', '),
                  resto:
                    alcance.total_personas > NOMBRES_A_LA_VISTA
                      ? t('perfilCargo.alcance.personasResto', {
                          resto: alcance.total_personas - NOMBRES_A_LA_VISTA,
                        })
                      : '',
                })
              : t('perfilCargo.alcance.sinPersonas')}
          </DialogDescription>
        </DialogHeader>
        <form className="space-y-4" noValidate onSubmit={enviar}>
          {alcance && (
            <p className="text-sm text-muted-foreground" data-testid="alcance-lineas">
              {alcance.version_anterior === null
                ? t('perfilCargo.alcance.lineasPrimera', {entran: alcance.lineas_entran})
                : t('perfilCargo.alcance.lineas', {
                    entran: alcance.lineas_entran,
                    salen: alcance.lineas_salen,
                    version: alcance.version_anterior,
                  })}
            </p>
          )}
          {alcance && alcance.criterios_faltantes.length > 0 && (
            <p className="text-sm text-destructive" data-testid="alcance-incompleto">
              {t('perfilCargo.alcance.faltaAntes', {
                falta: alcance.criterios_faltantes.map((criterio) => t(`perfilCargo.criterios.${criterio}`)).join(', '),
              })}
            </p>
          )}
          {alcance && <AlcanceRequisitos alcance={alcance} />}
          <div className="space-y-1">
            <DateField
              id="perfil-exigible"
              label={t('perfilCargo.alcance.exigibleDesde')}
              value={exigible}
              min={alcance?.hoy}
              onChange={(valor) => {
                setErrorFecha(null)
                onExigible(valor)
              }}
            />
            <FieldError error={errorFecha ? t(errorFecha) : undefined} />
          </div>
          <div className="space-y-2">
            <Label htmlFor="perfil-motivo">{t('perfilCargo.alcance.motivo')}</Label>
            <Textarea
              maxLength={tope('NOTA')}
              id="perfil-motivo"
              data-testid="perfil-motivo"
              value={motivo}
              onChange={(event) => onMotivo(event.target.value)}
            />
          </div>
          {/* Dentro del diálogo: en la página quedaría detrás del velo del modal. */}
          {errorFirma && (
            <p className="text-sm text-destructive" data-testid="perfil-alcance-error">
              {errorFirma}
            </p>
          )}
          <div className="flex gap-2">
            <Button
              type="submit"
              data-testid="perfil-confirmar"
              disabled={pendiente || (alcance?.criterios_faltantes.length ?? 0) > 0}>
              {t('perfilCargo.alcance.confirmar')}
            </Button>
            <Button type="button" variant="outline" onClick={() => onCerrar()}>
              {t('perfilCargo.alcance.cancelar')}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  )
}
